<template>
  <section class="error-bucket-viewer">
    <div class="viewer-actions">
      <button
        type="button"
        class="back-button"
        @click="$emit('back')"
      >
        <span class="material-icons-outlined" aria-hidden="true">
          arrow_back
        </span>

        Go Back
      </button>

      <button
        type="button"
        class="resolved-button"
        :disabled="!canResolve"
        :title="resolveButtonTitle"
        @click="resolveBucket"
      >
        {{ resolving ? "Resolving..." : "Resolved" }}
      </button>
    </div>

    <div
      v-if="resolutionError"
      class="resolution-error"
    >
      {{ resolutionError }}
    </div>

    <button
      type="button"
      class="bucket-summary"
      :class="bucketColorClass"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      <span class="material-icons-outlined bucket-chevron" aria-hidden="true">
        {{ expanded ? "expand_less" : "expand_more" }}
      </span>

      <span class="bucket-count">
        {{ bucket.count }}
      </span>

      <span class="bucket-copy">
        <strong class="bucket-error">
          {{ bucket.error }}
        </strong>

        <span class="bucket-location">
          {{ bucket.whErr }}
        </span>

        <span
          v-if="bucket.latestRecordedAt"
          class="bucket-recorded"
        >
          Latest ErrRecordedAt: {{ formatRecordedDate(bucket.latestRecordedAt) }}
        </span>
      </span>

      <span
        class="severity-pill"
        :class="bucket.severity"
      >
        {{ bucket.severity }}
      </span>
    </button>

    <div
      v-if="expanded"
      class="error-records"
    >
      <article
        v-for="(errorRecord, index) in sortedRecords"
        :key="errorRecord.id"
        class="error-record"
      >
        <span class="record-number">
          {{ index + 1 }}
        </span>

        <div>
          <span class="record-label">
            App Client
          </span>

          <strong>
            {{ errorRecord.lfClientId || "Unknown" }}
          </strong>
        </div>
      </article>
    </div>

    <section class="notes-section">
      <div class="notes-heading">
        <h3>Notes</h3>

        <button
          type="button"
          class="save-note-button"
          :disabled="
            savingNote ||
            loadingNotes ||
            !notesInitialized ||
            !noteContent.trim() ||
            !adminId ||
            !adminName
          "
          @click="submitNote"
        >
          {{ savingNote ? "Saving..." : "Save Note" }}
        </button>
      </div>

      <textarea
        v-model="noteContent"
        class="note-composer"
        rows="4"
        maxlength="10000"
        placeholder="Add a shared note about this error..."
        aria-label="Development error note"
      ></textarea>

      <div
        v-if="noteError"
        class="note-state error"
      >
        {{ noteError }}
      </div>

      <div
        v-if="loadingNotes"
        class="note-state"
      >
        Loading notes...
      </div>

      <div
        v-else-if="notes.length === 0"
        class="note-state"
      >
        No notes have been added to this error bucket.
      </div>

      <div
        v-else
        class="note-list"
      >
        <button
          v-for="note in notes"
          :key="note.id"
          type="button"
          class="note-card"
          :class="{ expanded: isNoteExpanded(note.id) }"
          :aria-expanded="isNoteExpanded(note.id)"
          @click="toggleNote(note.id)"
        >
          <span class="material-icons-outlined note-chevron" aria-hidden="true">
            {{ isNoteExpanded(note.id) ? "expand_less" : "expand_more" }}
          </span>

          <span class="note-copy">
            <span class="note-meta">
              <strong>{{ note.noteTakerName }}</strong>

              <span v-if="note.savedAt">
                {{ formatRecordedDate(note.savedAt) }}
              </span>
            </span>

            <span
              class="note-content"
              :class="{ expanded: isNoteExpanded(note.id) }"
            >
              {{ note.content }}
            </span>
          </span>
        </button>
      </div>
    </section>
  </section>
</template>

<script>
import {
  computed,
  onMounted,
  ref
} from "vue";

import {
  fetchDevelopmentErrorNotes,
  resolveDevelopmentErrorBucket,
  saveDevelopmentErrorNote
} from "@/firebase/firestore";

export default {
  name: "ErrorBucketViewer",

  props: {
    bucket: {
      type: Object,
      required: true
    },
    adminId: {
      type: String,
      required: true
    },
    adminName: {
      type: String,
      required: true
    }
  },

  emits: [
    "back",
    "resolved"
  ],

  setup(props, { emit }) {
    const expanded =
      ref(false);

    const notes =
      ref([]);

    const noteContent =
      ref("");

    const loadingNotes =
      ref(false);

    const savingNote =
      ref(false);

    const noteError =
      ref("");

    const expandedNoteIds =
      ref([]);

    const initialNoteCount =
      ref(0);

    const notesInitialized =
      ref(false);

    const sessionNoteCount =
      ref(0);

    const resolving =
      ref(false);

    const resolutionError =
      ref("");

    const sortedRecords =
      computed(() => [...props.bucket.records].sort((first, second) => {
        const firstTime = first.errRecordedAt?.getTime?.() ?? null;
        const secondTime = second.errRecordedAt?.getTime?.() ?? null;

        if (firstTime === null && secondTime === null) { return 0; }
        if (firstTime === null) { return 1; }
        if (secondTime === null) { return -1; }

        return secondTime - firstTime;
      }));

    const bucketColorClass =
      computed(() => {
        if (props.bucket.count >= 11) { return "heat-red"; }
        if (props.bucket.count >= 6) { return "heat-orange"; }
        if (props.bucket.count >= 3) { return "heat-yellow"; }

        return "heat-green";
      });

    const canResolve =
      computed(() => {
        return (
          !resolving.value &&
          sessionNoteCount.value > initialNoteCount.value
        );
      });

    const resolveButtonTitle =
      computed(() => {
        return canResolve.value
          ? "Resolve this error bucket."
          : "Add a new note during this session before resolving.";
      });

    const formatRecordedDate =
      date => date.toLocaleString([], {
        dateStyle: "medium",
        timeStyle: "short"
      });

    const loadNotes =
      async () => {
        loadingNotes.value = true;
        noteError.value = "";

        try {
          const loadedNotes = await fetchDevelopmentErrorNotes(props.bucket);

          notes.value = loadedNotes;

          if (!notesInitialized.value) {
            initialNoteCount.value = loadedNotes.length;
            sessionNoteCount.value = loadedNotes.length;
            notesInitialized.value = true;
          }
        } catch (error) {
          console.error("❌ Unable to load development error notes:", error);
          noteError.value = "Unable to load notes for this error bucket.";
        } finally {
          loadingNotes.value = false;
        }
      };

    const submitNote =
      async () => {
        const content = noteContent.value.trim();

        if (!content || savingNote.value || !notesInitialized.value) { return; }

        savingNote.value = true;
        noteError.value = "";

        try {
          await saveDevelopmentErrorNote(props.bucket, {
            content,
            noteTakerId: props.adminId,
            noteTakerName: props.adminName
          });

          noteContent.value = "";
          sessionNoteCount.value += 1;
          await loadNotes();
        } catch (error) {
          console.error("❌ Unable to save development error note:", error);
          noteError.value = "Unable to save this note.";
        } finally {
          savingNote.value = false;
        }
      };

    const resolveBucket =
      async () => {
        if (!canResolve.value) { return; }

        resolving.value = true;
        resolutionError.value = "";

        try {
          await resolveDevelopmentErrorBucket(props.bucket, {
            resolvedById: props.adminId,
            resolvedByName: props.adminName,
            resolvedOccurrenceCount: props.bucket.count,
            resolvedThroughRecordedAt: props.bucket.latestRecordedAt
          });

          emit("resolved");
        } catch (error) {
          console.error("❌ Unable to resolve development error bucket:", error);
          resolutionError.value = "Unable to resolve this error bucket.";
        } finally {
          resolving.value = false;
        }
      };

    const isNoteExpanded =
      noteId => expandedNoteIds.value.includes(noteId);

    const toggleNote =
      noteId => {
        if (isNoteExpanded(noteId)) {
          expandedNoteIds.value = expandedNoteIds.value.filter(id => id !== noteId);
          return;
        }

        expandedNoteIds.value = [...expandedNoteIds.value, noteId];
      };

    onMounted(loadNotes);

    return {
      expanded,
      sortedRecords,
      bucketColorClass,
      formatRecordedDate,
      notes,
      noteContent,
      loadingNotes,
      savingNote,
      noteError,
      notesInitialized,
      canResolve,
      resolveButtonTitle,
      resolving,
      resolutionError,
      submitNote,
      resolveBucket,
      isNoteExpanded,
      toggleNote
    };
  }
};
</script>

<style scoped>
.error-bucket-viewer {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.viewer-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.back-button,
.resolved-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 40px;
  padding: 0 16px;
  border-radius: 9px;
  font: inherit;
  font-weight: 600;
}

.back-button {
  border: 1px solid #d8dedb;
  background: #ffffff;
  color: #29342f;
  cursor: pointer;
}

.back-button .material-icons-outlined {
  font-size: 19px;
}

.resolved-button {
  border: 0;
  background: #159c62;
  color: #ffffff;
  cursor: not-allowed;
  opacity: 0.5;
}

.resolved-button:not(:disabled) {
  cursor: pointer;
  opacity: 1;
}

.resolution-error {
  padding: 12px 14px;
  border: 1px solid #efc4c4;
  border-radius: 9px;
  background: #fff7f7;
  color: #a33636;
  font-size: 0.88rem;
}

.bucket-summary {
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 18px;
  border: 1px solid #dde2df;
  border-radius: 12px;
  color: #26332d;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.bucket-summary.heat-green {
  border-color: rgba(38, 160, 93, 0.3);
  background: rgba(38, 160, 93, 0.08);
}

.bucket-summary.heat-yellow {
  border-color: rgba(215, 165, 0, 0.35);
  background: rgba(245, 194, 24, 0.13);
}

.bucket-summary.heat-orange {
  border-color: rgba(224, 112, 20, 0.35);
  background: rgba(243, 126, 32, 0.12);
}

.bucket-summary.heat-red {
  border-color: rgba(195, 45, 58, 0.35);
  background: rgba(220, 53, 69, 0.11);
}

.bucket-chevron {
  font-size: 25px;
}

.bucket-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  height: 42px;
  padding: 0 10px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.58);
  font-weight: 700;
}

.bucket-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
}

.bucket-error,
.bucket-location {
  overflow-wrap: anywhere;
}

.bucket-error {
  line-height: 1.4;
}

.bucket-location {
  color: #737d78;
  font-size: 0.9rem;
}

.bucket-recorded {
  color: #89928e;
  font-size: 0.78rem;
}

.severity-pill {
  padding: 6px 10px;
  border-radius: 999px;
  background: #eef1ef;
  color: #59645f;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.severity-pill.warning {
  background: #fff4d6;
  color: #916800;
}

.severity-pill.error,
.severity-pill.critical {
  background: #ffe4e4;
  color: #a52d2d;
}

.severity-pill.info {
  background: #e8f1ff;
  color: #315f9b;
}

.error-records {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.error-record {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 14px;
  padding: 15px 18px;
  border: 1px solid #e2e7e4;
  border-radius: 10px;
  background: #ffffff;
}

.record-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #eef4f0;
  color: #526059;
  font-size: 0.82rem;
  font-weight: 700;
}

.error-record div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.error-record strong {
  overflow-wrap: anywhere;
}

.record-label {
  color: #89928e;
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.notes-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 4px;
}

.notes-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.notes-heading h3 {
  margin: 0;
  font-size: 1rem;
}

.save-note-button {
  height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 9px;
  background: #159c62;
  color: #ffffff;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.save-note-button:disabled {
  cursor: default;
  opacity: 0.5;
}

.note-composer {
  width: 100%;
  min-height: 104px;
  padding: 13px 15px;
  border: 1px solid #d8dedb;
  border-radius: 10px;
  background: #ffffff;
  color: #29342f;
  box-sizing: border-box;
  font: inherit;
  line-height: 1.45;
  resize: vertical;
}

.note-composer:focus {
  border-color: #159c62;
  outline: 2px solid rgba(21, 156, 98, 0.12);
}

.note-state {
  padding: 16px 18px;
  border: 1px solid #e2e7e4;
  border-radius: 10px;
  background: #ffffff;
  color: #737d78;
}

.note-state.error {
  border-color: #efc4c4;
  background: #fff7f7;
  color: #a33636;
}

.note-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.note-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 14px;
  width: 100%;
  min-height: 68px;
  padding: 14px 18px;
  border: 1px solid #e2e7e4;
  border-radius: 10px;
  background: #ffffff;
  color: #29342f;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: min-height 0.18s ease, border-color 0.18s ease;
}

.note-card:hover {
  border-color: #aebbb4;
}

.note-card.expanded {
  align-items: start;
  min-height: 204px;
}

.note-chevron {
  font-size: 24px;
}

.note-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
}

.note-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: #89928e;
  font-size: 0.78rem;
}

.note-meta strong {
  color: #526059;
}

.note-content {
  display: -webkit-box;
  overflow: hidden;
  line-height: 1.45;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.note-content.expanded {
  display: block;
  overflow: visible;
  white-space: pre-wrap;
  -webkit-line-clamp: unset;
}

@media (max-width: 700px) {
  .bucket-summary {
    grid-template-columns: auto auto minmax(0, 1fr);
  }

  .severity-pill {
    grid-column: 3;
    justify-self: start;
  }
}
</style>
